"use client";

import { Thermometer, ShieldCheck, Wrench, ClipboardCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "ASSESS",
  Icon: Thermometer,
  description: "Our certified engineers conduct a comprehensive audit of your climate control infrastructure, identifying efficiency gaps and safety requirements."
}, {
  number: "02",
  title: "OPTIMIZE",
  Icon: Wrench,
  description: "We implement precision adjustments and necessary repairs, ensuring your systems operate at peak performance while meeting all regulatory standards."
}, {
  number: "03",
  title: "VERIFY",
  Icon: ShieldCheck,
  description: "Rigorous testing protocols confirm that every component functions safely, providing you with peace of mind and a comfortable environment."
}, {
  number: "04",
  title: "CERTIFY",
  Icon: ClipboardCheck,
  description: "We issue official compliance documentation, validating that your facility adheres to the latest industry safety and environmental regulations."
}];
export default function Compliance() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3883" id="gassystemcomplianceprocess" className="bg-slate-50 text-gray-900 relative overflow-hidden py-32 md:py-[200px]">
      {}
      <div className="pointer-events-none absolute top-0 left-4 hidden h-full lg:left-8 lg:block">
        <div className="sticky top-32">
          <div className="font-default text-[10px] font-medium tracking-[0.3em] uppercase text-gray-900/50" style={{
          writingMode: "vertical-rl"
        }}>
            00 &mdash; OUR PROCESS
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-20">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-blue-600" />
              <span className="font-default text-xs font-medium tracking-[0.25em] uppercase text-blue-600">
                Compliance Protocol
              </span>
            </div>
            <h2 className="font-default italic text-4xl leading-[1.05] tracking-tight text-gray-900 sm:text-5xl md:text-6xl lg:text-[64px]">
              Precision engineering
              <br />
              <span className="text-gray-900/70">
                for total climate safety.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-6">
            <p className="font-default text-lg leading-relaxed text-gray-900/75 md:text-xl">
              At Coastline Air Conditioning Services, we ensure your air conditioning systems meet the highest standards of safety and efficiency. Our systematic approach guarantees reliable climate control for your commercial or domestic space.
            </p>
            <p className="mt-5 font-default text-base leading-relaxed text-gray-900/60">
              No shortcuts. No risks. Only the professional, exacting standards of certified climate control experts.
            </p>
          </div>
        </div>

        {}
        <div className="relative mt-20 md:mt-28">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {steps.map((step, i) => {
            const card = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} ref={card.ref} className={`group relative transition-all duration-700 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {}
                  {i < steps.length - 1 && <div className="pointer-events-none absolute top-1/2 -right-3 z-20 hidden lg:block">
                      <div className="flex items-center gap-0.5">
                        <span className="h-px w-1.5 bg-blue-600" />
                        <span className="h-px w-1 bg-blue-600/60" />
                        <span className="h-px w-0.5 bg-blue-600/30" />
                      </div>
                    </div>}

                  {}
                  <div className="relative h-[380px] overflow-hidden border border-gray-200 bg-white backdrop-blur-xl shadow-lg transition-all duration-500 group-hover:-translate-y-1 group-hover:border-blue-600 group-hover:shadow-xl">
                    {}
                    <div className="absolute -top-4 -left-2 select-none">
                      <span className="font-default text-[120px] font-extrabold leading-none tracking-tighter text-gray-900/10 transition-colors duration-[400ms] group-hover:text-blue-600" aria-hidden="true">
                        {step.number}
                      </span>
                    </div>

                    {}
                    <div className="relative flex h-full flex-col justify-between p-8 pt-32">
                      <Icon className="h-10 w-10 text-gray-900/80 transition-colors duration-300 group-hover:text-blue-600" strokeWidth={1} />

                      <div>
                        <div className="mb-4 h-px w-8 bg-gray-900/30 transition-all duration-500 group-hover:w-16 group-hover:bg-blue-600" />
                        <h3 className="font-default text-2xl font-bold tracking-[0.15em] uppercase text-gray-900">
                          {step.title}
                        </h3>
                        <p className="mt-4 font-default text-[15px] leading-relaxed text-gray-900/65">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {}
                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-600 to-transparent" />
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {}
          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-gray-200 pt-8 sm:flex-row sm:items-center">
            <p className="font-default text-xs tracking-[0.25em] uppercase text-gray-900/50">
              Four steps &mdash; total compliance
            </p>
            <p className="font-default italic text-sm text-gray-900/60">
              Certified excellence for every installation.
            </p>
          </div>
        </div>
      </div>
    </section>;
}